/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

/**
 * NOTE: We intentionally do NOT use `@lexical/react/LexicalLinkPlugin` here.
 *
 * The reason is a dual-module-instance problem: `@inscript/lexical-editor`
 * depends on `@lexical/link@^0.38.x`, while the consuming application (or
 * `@inscript/react-template-editor`) may install `@lexical/react@0.33.x`,
 * which ships its own nested copy of `@lexical/link@0.33.x`.
 *
 * Lexical commands are identified by object identity (`===`). If
 * `TOGGLE_LINK_COMMAND` is imported from two different module instances it
 * becomes two distinct objects.  Dispatching with one object and registering
 * a handler for the other means the handler never fires – the dispatch always
 * returns `false`.
 *
 * By implementing the plugin ourselves and importing `TOGGLE_LINK_COMMAND`
 * and `$toggleLink` directly from `@lexical/link`, we guarantee that both
 * the dispatch site (`FloatingTextFormatToolbarPlugin`) and the handler
 * registration below resolve to the exact same module instance and therefore
 * the exact same `TOGGLE_LINK_COMMAND` object.
 */

import {$toggleLink, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {COMMAND_PRIORITY_LOW} from 'lexical';
import {useEffect} from 'react';

import {validateUrl} from '../../utils/url';

type Props = {
  hasLinkAttributes?: boolean;
};

export default function LinkPlugin({
  hasLinkAttributes = false,
}: Props): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const extraAttributes = hasLinkAttributes
      ? {rel: 'noopener noreferrer', target: '_blank'}
      : {};

    return editor.registerCommand(
      TOGGLE_LINK_COMMAND,
      (payload) => {
        if (payload === null) {
          $toggleLink(null);
          return true;
        }

        if (typeof payload === 'string') {
          // Standard string URL dispatch (e.g. from FloatingTextFormatToolbar)
          if (!validateUrl(payload)) {
            return false;
          }
          $toggleLink(payload, extraAttributes);
        } else {
          // Object payload: { url, ...linkAttributes } – pass through as-is
          $toggleLink(payload);
        }

        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, hasLinkAttributes]);

  return null;
}
